import React, { useState, useEffect } from 'react';
import { User, Clock, Mic, Settings } from 'lucide-react';
import { getBrandName, isKineticNotesAccount } from '../utils/brandUtils';
import { Account, Subscription, isTrialExpired as _isTrialExpired, getDaysRemaining as _getDaysRemaining, hasActiveSubscription as _hasActiveSubscription } from '../types';
import { api } from '../services/api';

interface NavbarProps {
  onLogout?: () => void;
  onProfile?: () => void;
  onUpgrade?: () => void;
  onSettings?: () => void;
  onMicConfiguration?: () => void;
  account?: Account;
}

export const Navbar: React.FC<NavbarProps> = ({ 
  onLogout: _onLogout, 
  onProfile: _onProfile,
  onUpgrade: _onUpgrade,
  onSettings: _onSettings,
  onMicConfiguration: _onMicConfiguration,
  account
}) => {
  const brandName = getBrandName();
  const [_subscription, setSubscription] = useState<Subscription | null>(null);
  const [_isLoadingSubscription, setIsLoadingSubscription] = useState(false);

  // Load subscription data
  useEffect(() => {
    if (account?.id) {
      setIsLoadingSubscription(true);
      api.getCurrentSubscription(account.id)
        .then((result) => {
          if ('status' in result && result.status === 'none') {
            setSubscription(null);
          } else {
            setSubscription(result as Subscription);
          }
        })
        .catch((error) => {
          console.error('Failed to load subscription:', error);
          setSubscription(null);
        })
        .finally(() => {
          setIsLoadingSubscription(false);
        });
    }
  }, [account?.id]);

  const _handleManageBilling = async () => {
    if (!account?.id) return;
   
    try {
      const returnUrl = window.location.origin;
      const response = await api.createPortalSession({
        accountId: account.id,
        returnUrl
      });
     
      window.location.href = response.url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const _getSubscriptionStatus = () => {
    if (_isLoadingSubscription) return 'Loading...';
   
    // Check if user has active subscription
    if (_subscription && (_subscription.status === 'active' || _subscription.status === 'trialing')) {
      switch (_subscription.status) {
        case 'active':
          return 'Pro';
        case 'trialing':
          return 'Trial';
        default:
          return 'Pro';
      }
    }
   
    // Check trial status for accounts without subscription
    if (account) {
      if (_isTrialExpired(account)) {
        return 'Trial Expired';
      }
     
      const daysRemaining = _getDaysRemaining(account);
      if (daysRemaining > 0) {
        return `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`;
      }
    }
   
    return 'Free';
  };

  const _getStatusBadgeColor = () => {
    if (_isLoadingSubscription) return 'badge badge-ghost text-primary-content px-3 py-1';
   
    if (_subscription && (_subscription.status === 'active' || _subscription.status === 'trialing')) {
      return 'badge badge-accent px-3 py-1';
    }
   
    if (account) {
      if (_isTrialExpired(account)) {
        return 'badge badge-accent text-white px-3 py-1';
      }
     
      const daysRemaining = _getDaysRemaining(account);
      if (daysRemaining > 0) {
        if (daysRemaining <= 2) {
          return 'badge px-3 py-1';
        }
        return 'badge px-3 py-1';
      }
    }
   
    return 'badge badge-ghost text-primary-content px-3 py-1';
  };

  const _isSubscriptionActive = _subscription?.status === 'active' || (account && _hasActiveSubscription(account));

  const closeDropdown = () => {
    // Close the dropdown by removing focus
    const elem = document.activeElement as HTMLElement;
    if (elem) {
      elem.blur();
    }
  };

  const handleMenuItemClick = (callback?: () => void) => {
    closeDropdown();
    if (callback) {
      callback();
    }
  };

  return (
    <div className="navbar bg-primary text-primary-content shadow-lg h-10 md:h-14 min-h-0">
      <div className="navbar-start">
        <div className="navbar-brand px-3 md:pl-4 flex items-center gap-2">
          {isKineticNotesAccount() && (
            <img 
              src="/kinetic-scribe-logo-32.png" 
              alt="Kinetic Scribe Logo" 
              className="h-4 w-4 md:h-5 md:w-5"
            />
          )}
          <h1 className="text-base md:text-xl font-semibold md:font-bold">{brandName}</h1>
        </div>
      </div>
      
      <div className="navbar-end px-3 md:px-4">
        {/* Subscription Status - Only show for non-active subscribers */}
        {!_isSubscriptionActive && (
          <div className="hidden md:flex items-center gap-2 mr-4">
            <div 
              className={_getStatusBadgeColor()}
              style={
                account && _getDaysRemaining(account) > 0 && !_hasActiveSubscription(account)
                  ? { backgroundColor: '#FFF4D8', color: '#8A6D3B' }
                  : undefined
              }
            >
              {account && _getDaysRemaining(account) > 0 && !_hasActiveSubscription(account) && (
                <Clock size={12} className="mr-1" />
              )}
              {_getSubscriptionStatus()}
            </div>
          </div>
        )}
        
        {/* User Menu */}
        <div className="dropdown dropdown-end">
          <div tabIndex={0} role="button" className="btn btn-ghost btn-circle text-primary-content hover:bg-primary-focus">
            <User size={20} />
          </div>
          <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
            <li>
              <div className="text-base-content/60 text-xs px-2 py-1">
                Plan: {_getSubscriptionStatus()}
              </div>
            </li>
            <div className="divider my-1"></div>
            <li>
              <button onClick={() => handleMenuItemClick(_onMicConfiguration)} className="text-base-content flex items-center gap-2">
                <Mic size={16} />
                Mic Configuration
              </button>
            </li>
            <li>
              <button onClick={() => handleMenuItemClick(_onSettings)} className="text-base-content flex items-center gap-2">
                <Settings size={16} />
                Settings
              </button>
            </li>
            <div className="divider my-1"></div>
            {!_isSubscriptionActive && (
              <li><button onClick={() => handleMenuItemClick(_onUpgrade)} className="text-base-content">Upgrade Plan</button></li>
            )}
            {_isSubscriptionActive && (
              <li><button onClick={() => handleMenuItemClick(_handleManageBilling)} className="text-base-content">Manage Billing</button></li>
            )}
            <div className="divider my-1"></div>
            <li><button onClick={() => handleMenuItemClick(_onLogout)} className="text-base-content">Logout</button></li>
          </ul>
        </div>
      </div>
    </div>
  );
}; 